import React, { useState, useMemo, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  FormControlLabel,
  List,
  styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchInput from "../Overview/components/SearchInput";
import CustomCheckbox from "../Overview/components/Checkbox";
import { getAvailableTags } from "../../Api/TagsApi";
import AppCache from "../../Helper/Common/AppCache";
import CloseIcon from "@mui/icons-material/Close";
import PrimaryChip from "../../Components/PrimaryChip";
import { PrimaryFormActionButton } from "../../Components/StyledComponent";

const ItemRowCheckBoxLabel = styled(FormControlLabel)(({ theme }) => ({
  display: "block",
  marginLeft: 0,

  ".MuiButtonBase-root": {
    padding: 0,
  },

  ".MuiCheckbox-root": {
    padding: 0,
    color: "#A2A2A2 !important",

    "&.Mui-checked": {
      color: "#494949 !important",
    },
  },

  ".MuiSvgIcon-root": {
    width: "12px",
    height: "12px",
  },
}));

const OperationalStatusFilter = ({ selectedOperationalStatus = [], onOperationalStatusChange }) => {
  const [search, setSearch] = useState("");
  const [tempSelected, setTempSelected] = useState(selectedOperationalStatus);
  const [appliedSelected, setAppliedSelected] = useState(selectedOperationalStatus);
  const [operationalStatusOptions, setOperationalStatusOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch operational status options on mount - using same API as global header filter
  useEffect(() => {
    const fetchOperationalStatus = async () => {
      try {
        setIsLoading(true);
        const res = await getAvailableTags();
        const tags = res?.response?.data?.tags || [];

        // Find the status tag (same as global filter)
        const statusTag = tags.find(tag => tag.tagCode === 'status');
        const rawList = statusTag?.supportedValues || [];

        const preAppliedGlobalFilters = AppCache.getItem("preAppliedGlobalFilters") || {};
        const excludeValues = preAppliedGlobalFilters["inventory.digitalTwinListTable"]?.excludeOperationalStatus || [];
        const operationalStatusList = excludeValues.length
          ? rawList.filter(item => !excludeValues.includes(item.valueCode))
          : rawList;

        // Store full objects with valueCode and displayName
        setOperationalStatusOptions(operationalStatusList);
      } catch (error) {
        console.error("Error fetching operational status:", error);
        setOperationalStatusOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOperationalStatus();
  }, []);

  // Get display name for a valueCode
  const getDisplayName = (valueCode) => {
    const option = operationalStatusOptions.find(opt => opt.valueCode === valueCode);
    return option ? option.displayName : valueCode;
  };

  // Get valueCode for a display name
  const getValueCode = (displayName) => {
    const option = operationalStatusOptions.find(opt => opt.displayName === displayName);
    return option ? option.valueCode : displayName;
  };

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return operationalStatusOptions;
    return operationalStatusOptions.filter((item) =>
      item.displayName.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, operationalStatusOptions]);

  const handleSelection = (option) => {
    const valueCode = option.valueCode;
    setTempSelected((prev) => {
      const alreadySelected = prev.some((code) => code === valueCode);
      const newSelected = alreadySelected
        ? prev.filter((code) => code !== valueCode)
        : [...prev, valueCode];

      return newSelected;
    });
  };

  const handleClearSelection = () => {
    setTempSelected([]);
  };

  const handleApply = () => {
    setAppliedSelected(tempSelected);
    if (onOperationalStatusChange) {
      onOperationalStatusChange(tempSelected);
    }
  };

  const handleChipDelete = (valueCode) => {
    const newSelected = appliedSelected.filter((x) => x !== valueCode);
    setAppliedSelected(newSelected);
    setTempSelected(newSelected);
    if (onOperationalStatusChange) {
      onOperationalStatusChange(newSelected);
    }
  };

  // Sync with parent's selected operational status
  useEffect(() => {
    setTempSelected(selectedOperationalStatus);
    setAppliedSelected(selectedOperationalStatus);
  }, [selectedOperationalStatus]);

  return (
    <Accordion
      sx={{
        boxShadow: "none",
        borderBottom: '1px solid #E6E6E6',
        my: 0,
        "&:before": { display: "none" },
        "&.Mui-expanded": {
          my: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          paddingTop: '18px',
          paddingBottom: '18px',
          alignItems: "flex-start",
          "&.Mui-expanded": {
            minHeight: "auto",
          },
          "& .MuiAccordionSummary-content": {
            margin: 0,
            alignItems: "flex-start",
            gap: 1,
          },
        }}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
          <Typography variant="fw400s14c000000" sx={{ lineHeight: '16px', color: "#1C1C1C", mr: 1 }}>
            Operational Status
          </Typography>
          {appliedSelected.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
              onClick={(e) => e.stopPropagation()} // prevent accordion toggle
            >
              {appliedSelected.map((valueCode) => (
                <PrimaryChip
                  key={valueCode}
                  label={getDisplayName(valueCode)}
                  tooltipTitle={getDisplayName(valueCode)}
                  tooltipPlacement="bottom"
                  onDelete={() => handleChipDelete(valueCode)}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Operational Status"
          />

          <Box sx={{ maxHeight: "200px", overflowY: "auto" }}>
            {isLoading ? (
              <Typography variant="fw400s12lh24c3D4B5A" sx={{ textAlign: "center", py: 2 }}>
                Loading operational status...
              </Typography>
            ) : filteredOptions.length === 0 ? (
              <Typography variant="fw400s12lh24c3D4B5A" sx={{ textAlign: "center", py: 2 }}>
                No operational status found
              </Typography>
            ) : (
              <List disablePadding>
                {filteredOptions.map((option, index) => (
                  <Box key={index} sx={{ py: 0.5 }}>
                    <ItemRowCheckBoxLabel
                      control={
                        <CustomCheckbox
                          checked={tempSelected.some((code) => code === option.valueCode)}
                          onChange={() => handleSelection(option)}
                        />
                      }
                      label={
                        <Typography variant="fw400s12lh24c3D4B5A">
                          {option.displayName}
                        </Typography>
                      }
                    />
                  </Box>
                ))}
              </List>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              mt: 1,
            }}
          >
            <Typography
              variant="fw500s12lh24c6D72F6"
              sx={{
                cursor: tempSelected.length > 0 ? "pointer" : "not-allowed",
                color: tempSelected.length > 0 ? "#6D72F6" : "#8F8F8F",
              }}
              onClick={() =>
                tempSelected.length > 0 ? handleClearSelection() : null
              }
            >
              Clear Selection
            </Typography>
            <PrimaryFormActionButton
              onClick={handleApply}
              sx={{py: 0.75, px: 2, fontSize: '12px'}}
            >
              Apply
            </PrimaryFormActionButton>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default OperationalStatusFilter;
